import { useEffect, useRef, useState } from 'react'
import type { CaseSample } from '../types'
import { IconInfo } from '../icons'
import { asset } from '../lib/asset'
import { DiffView } from './DiffView'

/**
 * 案例的前后对比：默认是放大镜，另有分割线与单看某一侧。
 *
 * 放大镜的手感搬自 ~/Documents/GitHub/Cases/前端/特效/图片对比放大镜
 * （原版出处 topazlabs.com/topaz-photo 首屏）：阻尼跟随、跟手快回位软、
 * 收敛后关掉 rAF、prefers-reduced-motion 下系数全给 1（功能必须活下来，动效可以全砍）。
 * 此前试过它的整套做法——两层图一起放大、镜片里换成处理后——见 Magnifier 上方为什么改掉。
 * 更早试过分割线加一块固定在屏幕右下角的放大格，那个形态要人一边看画面中间、
 * 一边看屏幕角落，两处画面的对应关系得靠脑补。
 *
 * 三件事是这个页面自己定的：
 *   1. 倍率是「原像素 100%」，不是参考实现那种随便定的 2.5 倍：把整幅缩放显示的图
 *      放回一个屏幕像素对一个图像像素。要判断的纹理周期只有 2 到 4 像素，
 *      非整数倍会触发插值，把周期本身改掉，于是屏幕上看到的周期不再是图里的周期。
 *      倍率档另给 2×、4×，都是原像素的整数倍。
 *   2. 不要长按才显示。原版按下去才算「亲手验证」；这里指针进入即捕获、离开即收回，
 *      按下留给切版本用。
 *   3. 一次只动一个变量：镜片只改倍率，按住只改版本。两个一起动的话，
 *      「纹理是变干净了还是只是被放大了」就说不清。
 *
 * 另外两条：
 *   - 收回之后图上什么都不留（2026-09-09 用户裁决：首页的预览不应该有红框，
 *     上传了之后才有）。诊断标出的位置只画在用户自己那张图上（见 ProblemMap）：
 *     那时红框回答的是「我这张图哪儿有问题」，而在首页案例上它回答的是别人的图，
 *     代价是把画面糊住一片——而这一屏要答的恰恰是「修完好不好看」。
 *     停靠点的计算保留着：?peek=1 的截图和键盘操作仍然从标记最密的那一格开始，
 *     落点由诊断记录算出，不是人挑的。
 *   - 淡入淡出只在放大过程中出现，稳态下两层之间没有任何混合。半透明会把两张图
 *     叠在一起，而叠出来的像素哪张图里都没有；同理不加 filter 与混合模式。
 */

/** 镜片边长（CSS 像素）。容器装不下时按容器缩。 */
const BOX = 300

/**
 * 倍率档，单位是「原像素的几倍」。1 就是一个屏幕像素对一个图像像素。
 *
 * 只给整数（见上文第 2 条）。默认落在 1 是实测比出来的：叠瓦格子在原图上边长约
 * 167 像素，300 见方的镜片在 1 倍下装得下一格半，「成片重复」才成立；
 * 4 倍下只装进不到半格，看到的是一格内部的糊面，前后都糊，于是差别反而消失。
 *
 * 2026-09-09 用户裁决：「只给 100 和 2x 吧。」原先还有 4 倍，留着是为了看格子纹
 * （周期只有 2 到 4 像素）。2 倍下那个周期已经是 4 到 8 个屏幕像素，看得见，
 * 而每多一档就多一次「该按哪个」的选择，选错了（4 倍看叠瓦）反而看不出差别。
 */
const ZOOMS = [1, 2]

/**
 * 缓动系数：每帧向目标推进的比例。数越小越黏。
 *
 * 跟手要快、回位要软，这是参考实现里很划算的一个细节：两头用同一个数的话，
 * 收回时镜片会「啪」地弹回停靠点。系数不是时长，所以高刷屏上整体更利落——
 * 这是这类写法的固有代价，为了短和好读留着它。
 */
const EASE = { follow: 0.26, home: 0.1, fadeIn: 0.22, fadeOut: 0.16 }

/** 目标与当前的距离小于这个值就认为到位，收掉 rAF 循环。 */
const SETTLE = { pos: 0.05, alpha: 0.004 }

/** 键盘每次移动的步长，按住 Shift 走三倍。 */
const KEY_STEP = 36

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v)

/**
 * 放大镜是否强制展开，由 URL 的 ?peek=1 打开。
 *
 * 它只为无头截图存在：放大靠悬停触发，而 Chrome 的 --screenshot 无法模拟悬停，
 * 于是这个功能在截图里永远是收着的，评审看不到它长什么样。
 */
const FORCE_OPEN = new URLSearchParams(window.location.search).has('peek')

/**
 * 这个板子要的只是「两张图 + 一个落点」，所以它收的是 CaseSample 的一个子集，
 * 而不是整条案例。用户自己那张作业也走这里（2026-09-11 用户裁决：
 * 「还要给处理前后的对比，就和首页一样」），而作业没有 kind、model、provenance
 * 这些属于案例库的字段——按整条案例收，那边就得现编几个假值。
 */
export type ComparePair = Pick<CaseSample, 'title' | 'before' | 'after' | 'hotspot'>
type CompareView = 'mag' | 'wipe' | 'before' | 'after' | 'diff'

export function CaseCompare({ sample, solo = false, diff, initialView = 'mag' }: {
  sample: ComparePair
  /**
   * 只有一对图、不会中途换（结果页就是这样）。
   *
   * 首页那个图槽的高度是按**五条案例里最高的那条**钉死的，为的是点标签换案例时
   * 页面不跳。结果页没有标签可点，钉着那个高度只会在图下面空出一大条
   * （2026-09-11 走查截图里看得见）。
   */
  solo?: boolean
  /**
   * 给了就多一档「差分」：逐像素比对，把这次动过的地方点亮（2026-09-11 用户裁决
   * 「最好给用户能看的是差分图，这样心里更有数」）。
   *
   * 只在前后两张是同一幅画的两次采样时给。走过深度修复的成品是重画的，
   * 相减出来是两张不同的画；录像那一档的前后是两次运行各自导出的 JPEG，同理。
   * `readings` 是服务端在成品上现算的实测值，前端不重算——图上那层是定标后的亮度，
   * 回答「动在哪」；实测值回答「动了多少」，两者不能互相替代。
   */
  diff?: { readings?: { maxLevels: number; meanLevels: number; changedPct: number } }
  /**
   * 首页案例仍从放大镜开始；结果页传 wipe，让用户先看整幅的分割线对比。
   * 这里只决定第一次打开，之后仍完全听下面四颗按钮的。
   */
  initialView?: CompareView
}) {
  const [view, setView] = useState<CompareView>(initialView)
  const [zoom, setZoom] = useState(1)
  const [diffStats, setDiffStats] = useState<{ levels: number; gain: number } | null>(null)
  /**
   * 差分算不出来（前后尺寸不一样、图取不回来、两张根本不是同一幅画的两次采样）。
   *
   * 这一档本来只挂在无损那一路上，算不出来属于不该发生的事；可一旦发生，
   * 画布是空的，而槽里垫着的「处理前」照常显示——用户看到的就是「点了差分，
   * 什么都没变」（2026-09-11 实跑：「差分没显示东西」）。所以宁可把话说出来。
   */
  const [diffBroken, setDiffBroken] = useState(false)

  const [bw, bh] = sample.before.pixels
  const [aw, ah] = sample.after.pixels
  /**
   * 前后尺寸差得能看出来（超过 1%）时要明说。
   *
   * 深度修复是重新出图，成品尺寸由生成接口定，链 H 就从 1280×720 变成 1672×941。
   * 两张按同一宽度并排，成品等于被多缩了三成，纹理会系统性地显得更细——
   * 那一部分「变干净」不是修出来的。这句话曾经是画面角标上的两个像素数，
   * 2026-09-09 用户裁决把角标上的读数撤了（分辨率、倍率都不提），
   * 于是这件事挪到图下说：撤的是标签上的技术读数，不是这个事实。
   * K、F、H25 三条前后差千分之四，这句不出现。
   *
   * 哪一侧被多缩了要按实际方向说。链 H 是成品更大（成品被多缩、显得更细，
   * 这一下偏向我们）；夜景长发反过来，处理前那张更大（1579 → 1520），
   * 被多缩的是**处理前**，于是脏图会显得比它实际上干净。
   * 两种都得照实说：只说对自己不利的那一种，就是挑着说。
   */
  const resized = Math.abs(aw / bw - 1) > 0.01 || Math.abs(ah / bh - 1) > 0.01

  // 这里原先要预载一张单独的原像素图（约 700 KB），镜片等它落地之前先拿概览图顶着，
  // 图上还挂一枚「正在载入原像素图…」。概览图取消之后（一侧只有一个文件，
  // 见 types.ts 的 CaseSide）镜片和整幅用的是同一个 URL：整幅那张 <img> 一落地，
  // 镜片的背景就已经在缓存里，没有第二次加载可等，那套状态一并删掉。

  return (
    <div className={solo ? 'cmp cmp-solo' : 'cmp'}>
      {/* 图放在一个高度固定的槽里。
          槽高由**五条案例里最高的那一条**定（`--slot-ar`，见 Landing）：
          四条链是 16:9，夜景长发是 19:12，高出 85 像素。没有这个槽的话，
          点一下案例标签，图下面的工具条、提示与页脚会整体上下跳一截
          （2026-09-10 用户裁决：这个按钮不应该跳页面的位置）。
          换图时的加载空档同理——图没解码前高度是 0，页面会先塌一下再弹回来。
          矮的四条顶对齐，余量留在图下，不上下均分——均分会在标签底下
          空出一截，看起来像这一区还没收紧（2026-09-10：「可以再紧凑一些」）。
          槽高不变，所以工具条和它下面的东西仍然不跳。
          槽只管尺寸，`.mag` 仍然紧贴图本身（width: fit-content），
          所以镜片那套按图的 rect 算的坐标不受影响。 */}
      <div className="cmp-slot">
        {view === 'mag' ? <Magnifier sample={sample} zoom={zoom} />
          : view === 'diff' ? (
            /* 差分画布压在处理前那张上：那张 <img> 只用来定框，让四档视图的取景完全一致，
               切换时画面不会跳一下（visibility 而不是 display，框才留得住）。 */
            <div className="cmp-view is-diff">
              <img className="cmp-base" src={asset(sample.before.src)} alt="" aria-hidden />
              <DiffView
                beforeSrc={asset(sample.before.src)}
                afterSrc={asset(sample.after.src)}
                onStats={s => { setDiffStats(s); setDiffBroken(!s) }}
              />
              {diffStats && <span className="cmp-tag cmp-tag-r">差分 ×{diffStats.gain}</span>}
            </div>
          )
          : <Wipe sample={sample} view={view} />}
      </div>

      <div className="cmp-bar">
        <div className="seg">
          <button className={view === 'mag' ? 'on' : ''} onClick={() => setView('mag')}>放大镜</button>
          <button className={view === 'wipe' ? 'on' : ''} onClick={() => setView('wipe')}>分割线</button>
          <button className={view === 'before' ? 'on' : ''} onClick={() => setView('before')}>只看处理前</button>
          <button className={view === 'after' ? 'on' : ''} onClick={() => setView('after')}>只看处理后</button>
          {diff && (
            <button className={view === 'diff' ? 'on' : ''} onClick={() => setView('diff')}>差分</button>
          )}
        </div>
        {view === 'mag' && (
          <div className="seg">
            {ZOOMS.map(z => (
              <button key={z} className={zoom === z ? 'on' : ''} onClick={() => setZoom(z)}>
                {z === 1 ? '原像素 100%' : `${z}× 原像素`}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 提示只有一段。
          原先这里是三行：整幅为什么看不见纹理、镜片里外分别是哪一层、按下会发生什么。
          那是把交互说明书贴在了图下面（2026-09-10 用户裁决：界面太啰嗦）。
          怎么用已经写在图上那枚药丸里，鼠标一移就自证；这里只留「镜片里是原像素」
          这一件不看图看不出来的事。

          「整幅是缩小显示的」这句仍然成立，注意它现在指的是**浏览器在显示时缩**：
          文件本身是原像素、没有预缩（见 types.ts 的 CaseSide）。1 倍屏上 1240 CSS
          像素装不下 1672 的图，那一遍缩放照样会抹掉 2 到 4 像素的周期。

          尺寸那句**并进同一段**，不另起一行：它只在苔藓峡谷那条出现，
          单独一行会让页面在切到那条时多长出一行、其余的内容跟着往下挪
          （同一个「不要跳」的裁决）。并进来之后行数不变。
          它也不能删——H 修复后出图尺寸变了三成，两张按同一宽度显示时成品被多缩了一些，
          纹理天然显得更细，不说这条案例就是在占便宜。 */}
      <p className="cmp-hint">
        <IconInfo size={15} />
        <span>
          {view === 'diff'
            /* 这一档要把三件事一次说清：亮的是什么、为什么要放大、实测动了多少。
               不说放大倍数，这张全是亮点的图会被读成「画面被改得面目全非」，
               与我们「无损」的说法自相矛盾。 */
            ? diffBroken
              ? '这一档算不出来：前后两张不是同一幅画的两次采样（尺寸不同，或者取不回原图），逐像素相减没有意义。上面看到的是处理前那张。'
              : (<>
                {'亮的地方是这次动过的像素——被清掉的那层细网格就长这样。'}
                {diffStats
                  ? `差值最深只有 ${diffStats.levels} 个色阶，这里放大了约 ${diffStats.gain} 倍才画得出形状。`
                  : '正在逐像素比对……'}
                {diff?.readings
                  ? `逐像素比对：平均差 ${diff.readings.meanLevels} 个色阶，看得出差别的像素占 ${diff.readings.changedPct}%。`
                  : ''}
                {'成品上这层是被减掉的，不是被加上去的。'}
              </>)
            : (<>
          {view === 'mag'
            ? '整幅是缩小显示的，镜片里才是原像素。'
            : '整幅看构图，纹理要切到放大镜看。'}
          {resized
            ? `这条修复后出图尺寸变了（${bw}×${bh} → ${aw}×${ah}），两张按同一宽度显示，`
              + (aw > bw
                ? '所以成品被多缩了一些，纹理会显得更细一点。'
                : '所以处理前那张被多缩了一些，纹理会显得比实际细一点。')
            : ''}
              </>)}
        </span>
      </p>
    </div>
  )
}

/**
 * 放大镜。背景不动，镜片放大它底下那一块。
 *
 * 2026-09-09 用户裁决：应该是放大镜，背景不要放大。
 * 此前照参考实现把两层图一起放大（镜片里处理后、镜片外处理前），那样镜片边缘同时
 * 换了版本又换了倍率，两个变量一起动，「纹理是变干净了还是只是被放大了」就说不清；
 * 而且整幅一放大，构图也跟着没了——而这一屏还要答「修完好不好看」。
 * 现在只有镜片里那一块放大：**镜片里是处理后的原像素，镜片外是处理前的整幅**，
 * 整幅始终是原尺寸显示，构图还在。
 * 按下时镜片收起来，整幅换成处理后（2026-09-09 用户裁决：按下的时候不要放大镜了）——
 * 成品该整幅地看，不该再隔着一块玻璃。
 *
 * 镜片的边缘同时换了版本和倍率，所以它回答的是「这块地方修完长什么样」，
 * 不是「粗细变了多少」。后者靠按住鼠标闪一下：那一下位置不动、两幅都是整幅，
 * 只差处理这一件事。
 *
 * 位置跑在一个 rAF 循环里，收敛后自己关掉，静止时不空转。每帧只写内联样式、
 * 不测量布局——尺寸由 ResizeObserver 提供。参考实现记载原版每帧调
 * getBoundingClientRect 加 getComputedStyle，等于每帧强制同步布局一次。
 */
function Magnifier({ sample, zoom }: { sample: ComparePair; zoom: number }) {
  const root = useRef<HTMLDivElement>(null)
  const afterEl = useRef<HTMLDivElement>(null)
  const lensEl = useRef<HTMLDivElement>(null)
  const pillEl = useRef<HTMLSpanElement>(null)
  const [open, setOpen] = useState(FORCE_OPEN)
  // 只为角标与光标形状而存在的副本；每帧的画由 st.current.pressed 决定，
  // 因为按下时先叫醒的是 rAF，它可能跑在 React 重渲染之前。
  const [pressed, setPressed] = useState(false)

  // 每帧要用但不该触发重渲染的量，全部放 ref：改它们只是重画一帧。
  const geo = useRef({ w: 0, h: 0 })
  const st = useRef({
    x: 0, y: 0, aimX: 0, aimY: 0,
    a: FORCE_OPEN ? 1 : 0, aimA: FORCE_OPEN ? 1 : 0,
    // client 是最后一次指针的视口坐标。没有指针来过时它是 null——
    // 拿 [0, 0] 当初值的话，第一次布局同步会把镜片按「指针在视口左上角」算，
    // 于是 ?peek=1 的截图里镜片贴在图的左上角，而不是诊断标出的那一格。
    open: FORCE_OPEN, pressed: false, raf: 0, client: null as [number, number] | null,
  })
  const cfg = useRef({ zoom })
  cfg.current = { zoom }
  // 背景图每帧重写会让浏览器反复解析同一个 URL，记住上次那个，只在换版本时写。
  const lastSrc = useRef('')
  // 循环由事件唤醒，而倍率是从外面改的，得留一个把手给它。
  const wake = useRef<() => void>(() => {})

  useEffect(() => {
    const el = root.current
    if (!el) return
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)')
    const easeOf = (v: number) => (reduced.matches ? 1 : v)

    /** 镜片边长。容器装不下 BOX 时按容器缩，否则镜片会比图还大。 */
    const boxSize = () => {
      const { w, h } = geo.current
      return Math.max(80, Math.min(BOX, w - 24, h - 24))
    }

    /**
     * 停靠点：诊断标出的、标记最密的那一格。不夹到画框内侧，理由同下。
     */
    const home = (): [number, number] => [
      (sample.hotspot[0] - 0.5) * geo.current.w,
      (sample.hotspot[1] - 0.5) * geo.current.h,
    ]

    const paint = () => {
      const { w, h } = geo.current
      const s = st.current
      const lens = lensEl.current
      if (!w || !h || !lens) return
      const box = boxSize()
      const half = box / 2

      // 按住时整幅换成处理后，同一帧里镜片收掉。分两处改的话会有一帧
      // 「镜片还在、底下已经是成品」，那一帧看起来就像镜片凭空修好了一块。
      if (afterEl.current) afterEl.current.style.opacity = s.pressed ? '1' : '0'

      lens.style.width = `${box}px`
      lens.style.height = `${box}px`
      lens.style.transform = `translate(${s.x - half}px, ${s.y - half}px)`
      // 收掉是直接切 0，不缓动：这一下要跟版本切换同帧完成，
      // 淡出的话会有几帧半透明的镜片压在成品上，那几帧里的像素两张图里都没有。
      lens.style.opacity = String(s.pressed ? 0 : s.a)

      // 镜片里是处理后的原像素：背景按那张图的原始尺寸铺开（乘倍率），再挪到指针那一块。
      // 落点按相对坐标换算，不按像素：两张图走过深度修复，尺寸差千分之四。
      //
      // 偏移不夹。镜片已经贴着指针走，夹了偏移就等于「镜片在这儿、里面却是别处」，
      // 靠边时更明显：那才是真的看不出放大。不夹也不会露出底色——
      // 镜片溢出画框的那一截是 half - fx×w，取景缺的那一截是 half - fx×bgW，
      // 而 bgW = 原始宽 × 倍率 ≥ w（.mag-img 是 width:auto / max-width:100%，
      // 只会缩不会放，倍率又 ≥ 1），所以缺的永远小于被裁掉的，落在看不见的地方。
      const side = sample.after
      const bgW = side.pixels[0] * cfg.current.zoom
      const bgH = side.pixels[1] * cfg.current.zoom
      const fx = (s.x + w / 2) / w
      const fy = (s.y + h / 2) / h
      const ox = fx * bgW - half
      const oy = fy * bgH - half
      // 与整幅同一个文件（原像素、未缩放）。整幅那张 <img> 已经把它拉进缓存，
      // 镜片这边不会再发一次请求，也没有「先糊一下再变清」的中间态。
      const src = asset(side.src)
      if (src !== lastSrc.current) {
        lens.style.backgroundImage = `url("${src}")`
        lastSrc.current = src
      }
      lens.style.backgroundSize = `${bgW}px ${bgH}px`
      lens.style.backgroundPosition = `${-ox}px ${-oy}px`

      // 「处理后」这个标签挂在镜片露在画框内那一半的底部中线上，不是镜片自己的中线。
      // 镜片贴边时有小半块在画框外，按镜片中线摆的话标签正好跨在画框边上，
      // 被裁掉半个字——而画面上此刻只剩这一处告诉人镜片里是哪个版本。
      // 指针总在图内，所以可见的部分至少有半块镜片（150 像素），装得下这三个字。
      if (pillEl.current) {
        const vx0 = Math.max(0, half - (s.x + w / 2))
        const vx1 = box - Math.max(0, s.x + w / 2 + half - w)
        const vy1 = box - Math.max(0, s.y + h / 2 + half - h)
        pillEl.current.style.transform =
          `translate(calc(-50% + ${(vx0 + vx1) / 2 - half}px), ${vy1 - box}px)`
      }
      // 硬边像素只在放大到 2 倍以上时开。100% 是一个屏幕像素对一个图像像素，
      // 本来就没有插值；而放大后必须关掉平滑，否则 2 像素的周期会被糊掉，
      // 于是镜片显示的纹理不再是图里的纹理。
      lens.classList.toggle('is-pixel', cfg.current.zoom > 1)
    }

    const tick = () => {
      const s = st.current
      const posEase = easeOf(s.open ? EASE.follow : EASE.home)
      s.x += (s.aimX - s.x) * posEase
      s.y += (s.aimY - s.y) * posEase
      s.a += (s.aimA - s.a) * easeOf(s.open ? EASE.fadeIn : EASE.fadeOut)

      // 收敛判定要把淡入淡出一起算进去。只判位置的话，收回时镜片早就归位、
      // 透明度还在半路，循环停在那里，图上就留着一块半透明的镜片。
      const settled =
        Math.abs(s.aimX - s.x) < SETTLE.pos
        && Math.abs(s.aimY - s.y) < SETTLE.pos
        && Math.abs(s.aimA - s.a) < SETTLE.alpha
      if (settled) {
        s.x = s.aimX; s.y = s.aimY; s.a = s.aimA
        paint()
        s.raf = 0
        return
      }
      paint()
      s.raf = requestAnimationFrame(tick)
    }

    // 收敛后循环会自己关掉，静止时不空转；下一次改动把它叫醒。
    // 若此时其实已经到位，tick 会画一帧再关掉，正好用来响应倍率变化。
    const kick = () => { if (!st.current.raf) st.current.raf = requestAnimationFrame(tick) }
    wake.current = kick

    /**
     * 镜片对准指针。只夹到画框边，不夹到「整块镜片都留在图内」。
     *
     * 2026-09-09 用户裁决：指针移到边缘，镜片也要跟到边缘。
     * 此前夹的是镜片中心，于是沿四边各有半个镜片宽的死区（实测 1092×615 的显示上
     * 是横向 14%、纵向 24%）：指针进了那一圈，镜片就停在死区边界不再跟手，
     * 而最外侧那一条的原像素在镜片里永远看不到——恰恰是「靠边的地方修没修好」
     * 完全没法验证。现在镜片贴着指针走，超出画框的那半截由容器的 overflow 裁掉。
     * 夹到画框边是为了指针捕获期间（按住往图外拖）镜片不会整块飞出去。
     */
    const aimAt = (clientX: number, clientY: number) => {
      const r = el.getBoundingClientRect()
      st.current.client = [clientX, clientY]
      st.current.aimX = clamp(clientX - r.left - r.width / 2, -r.width / 2, r.width / 2)
      st.current.aimY = clamp(clientY - r.top - r.height / 2, -r.height / 2, r.height / 2)
      kick()
    }

    /**
     * 展开镜片。
     *
     * snap 为真时位置直接落到目标，不缓动过去：收回之后镜片是不可见的，
     * 再进来若从上一次的位置飞过来，用户看到的是一块凭空出现的画面在滑行。
     */
    const engage = (snap = false) => {
      st.current.aimA = 1
      if (snap) { st.current.x = st.current.aimX; st.current.y = st.current.aimY }
      if (!st.current.open) {
        st.current.open = true
        setOpen(true)
      }
      kick()
    }

    /** 收回。指针离开就整个消失，不留停靠的镜片（2026-09-09 用户裁决）。 */
    const release = () => {
      setPressed(false)
      st.current.pressed = false
      if (!st.current.open || FORCE_OPEN) return
      st.current.open = false
      st.current.aimA = 0
      setOpen(false)
      kick()
    }

    const onEnter = (e: PointerEvent) => { aimAt(e.clientX, e.clientY); engage(true) }
    const onMove = (e: PointerEvent) => { if (st.current.open) aimAt(e.clientX, e.clientY) }

    /**
     * 按下：镜片收起来，整幅换成处理后（2026-09-09 用户裁决）。
     *
     * 「鼠标按下之后就是新内容」，而新内容要整幅地看——挑毛病用镜片，看成品不用。
     * 松手回到处理前和镜片，于是按下与松手之间只差处理这一件事，眼睛不用移动：
     * 闪一下比盯着看灵敏得多，成片纹理的有无，眼睛对「变化」比对「差异」敏感。
     * 触屏没有悬停，按下是它唯一的入口，所以那里按下仍然是「展开镜片」并拖动。
     */
    const onDown = (e: PointerEvent) => {
      e.preventDefault()
      aimAt(e.clientX, e.clientY)
      engage(!st.current.open)
      if (e.pointerType === 'mouse') {
        st.current.pressed = true
        setPressed(true)
        kick()
      }
      try { el.setPointerCapture(e.pointerId) } catch { /* 捕获失败不影响，下面有兜底 */ }
    }

    /** 松手回到处理前和镜片。指针仍在图上，所以镜片不收回。 */
    const onUp = () => {
      if (!st.current.pressed) return
      st.current.pressed = false
      setPressed(false)
      // 按住期间镜片不可见，位置照样跟手，但缓动落后的那一截看不见也就补不掉；
      // 直接落到指针处，免得松手那一下镜片从上一处飞过来。
      st.current.x = st.current.aimX
      st.current.y = st.current.aimY
      kick()
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        // 键盘没有「按住」，改成粘滞开关：一次切处理后，再一次切回来。
        e.preventDefault()
        engage()
        st.current.pressed = !st.current.pressed
        setPressed(st.current.pressed)
        kick()
        return
      }
      if (e.key === 'Escape') { release(); return }
      const step = (e.shiftKey ? 3 : 1) * KEY_STEP
      const d: Record<string, [number, number]> = {
        ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step],
      }
      const move = d[e.key]
      if (!move) return
      e.preventDefault()
      engage()
      st.current.aimX = clamp(st.current.aimX + move[0], -geo.current.w / 2, geo.current.w / 2)
      st.current.aimY = clamp(st.current.aimY + move[1], -geo.current.h / 2, geo.current.h / 2)
      kick()
    }

    // 尺寸或滚动位置变了，上一次按视口坐标算出的偏移就不对了；
    // 没展开时镜片按停靠点摆好（虽然此刻是透明的），下一次展开才不用从别处飞过来。
    const resync = () => {
      const r = el.getBoundingClientRect()
      geo.current = { w: r.width, h: r.height }
      const last = st.current.client
      if (st.current.open && last) aimAt(last[0], last[1])
      else {
        const [hx, hy] = home()
        st.current.aimX = hx; st.current.aimY = hy
        st.current.x = hx; st.current.y = hy
      }
      paint()
    }

    resync()
    el.addEventListener('pointerenter', onEnter)
    el.addEventListener('pointermove', onMove)
    el.addEventListener('pointerleave', release)
    el.addEventListener('pointerdown', onDown)
    el.addEventListener('pointerup', onUp)
    el.addEventListener('pointercancel', release)
    // 兜底。指针捕获万一没拿到，松手事件不会回到这里，画面就卡在整幅处理后，
    // 而那时页面正拿着成品冒充「你上传的图」。
    window.addEventListener('pointerup', onUp)
    el.addEventListener('keydown', onKey)
    el.addEventListener('blur', release)
    window.addEventListener('scroll', resync, { passive: true })
    const ro = new ResizeObserver(resync)
    ro.observe(el)

    return () => {
      if (st.current.raf) cancelAnimationFrame(st.current.raf)
      st.current.raf = 0
      el.removeEventListener('pointerenter', onEnter)
      el.removeEventListener('pointermove', onMove)
      el.removeEventListener('pointerleave', release)
      el.removeEventListener('pointerdown', onDown)
      el.removeEventListener('pointerup', onUp)
      el.removeEventListener('pointercancel', release)
      window.removeEventListener('pointerup', onUp)
      el.removeEventListener('keydown', onKey)
      el.removeEventListener('blur', release)
      window.removeEventListener('scroll', resync)
      ro.disconnect()
    }
  }, [sample])

  // 倍率改了要立刻重画一帧。它不会触发 ResizeObserver，
  // 不叫醒循环的话得等下一次指针移动才生效。
  useEffect(() => { wake.current() }, [zoom])

  return (
    <div
      className={`mag${open ? ' is-open' : ''}${pressed ? ' is-pressed' : ''}`}
      ref={root}
      tabIndex={0}
      role="group"
      aria-label={`${sample.title}：镜片里是处理后的原像素，按住看整幅处理后`}
    >
      {/* 两层都是原像素图，交给浏览器缩到显示宽度——不再喂预先缩好的概览图
          （2026-09-10 用户裁决：不要压缩，要保持锐利）。
          上层盖满、由 opacity 开关整幅切换，不做过渡：半透明会把两张图叠在一起，
          而叠出来的像素哪张图里都没有。 */}
      <img className="mag-img" src={asset(sample.before.src)} alt="处理前" />
      <div className="mag-after" ref={afterEl} style={{ opacity: pressed ? 1 : 0 }}>
        <img src={asset(sample.after.src)} alt="处理后" />
      </div>
      {/* 内白外黑的双层描边：画面有亮部也有暗部，单色描边必然在其中一边糊掉。 */}
      <div className="lens" ref={lensEl}>
        {/* 标签只写版本，不写分辨率与倍率（2026-09-09 用户裁决）。
            倍率在工具条上就是选中的那一档，写第二遍是重复；分辨率是产物参数，
            首页要答的是「修成什么样」，两个尺寸摆在这里只会让人去比这两个数。 */}
        <span className="mag-pill" ref={pillEl}>处理后</span>
      </div>
      {/* 角标跟着画面走：按住时整幅是成品，还写「处理前」就是在指着成品说这是原图。 */}
      <span className="cmp-tag cmp-tag-l">{pressed ? '处理后' : '处理前'}</span>
      {/* 收回之后图上没有任何痕迹，得留一句话告诉人这里可以动。
          它只在没展开时出现，展开后立刻让位给画面。 */}
      {/* 图上这一句是「怎么用」，用户给的原话（2026-09-10：「鼠标长按图片快速预览对比」）。
          它把两个动作压成一句：鼠标移上来先有镜片，长按则整幅切到处理后。
          先前写的是「鼠标移上来看修好的原像素 · 按住看整幅」——把机制说全了，
          代价是窄窗口下这行字比图还宽，直接顶出画面（用户截图为证）。
          现在短了，宽度也在 CSS 里兜了底（.mag-cue 的 max-width）。 */}
      {!open && <span className="mag-cue">鼠标长按图片，快速预览对比</span>}
    </div>
  )
}
/**
 * 分割线：看整幅的构图与整体观感。
 *
 * 留着它是因为放大镜只擅长一件事——看清一处局部差异。整体的观感变化
 * （比如整幅的通透程度）透过一个小窗口看等于什么都没看到。
 */
function Wipe({ sample, view }: { sample: ComparePair; view: 'wipe' | 'before' | 'after' }) {
  const [pct, setPct] = useState(50)
  const [locked, setLocked] = useState(false)

  // 上层（处理后）的可见范围。100 是整幅裁掉，只剩下层；0 是整幅覆盖。
  const topClip = view === 'wipe' ? pct : view === 'before' ? 100 : 0
  const showLeftTag = view !== 'after' && !(view === 'wipe' && pct < 14)
  const showRightTag = view !== 'before' && !(view === 'wipe' && pct > 86)

  return (
    <div
      className="cmp-view"
      onPointerMove={e => {
        if (locked || view !== 'wipe') return
        const r = e.currentTarget.getBoundingClientRect()
        if (r.width) setPct(clamp(((e.clientX - r.left) / r.width) * 100, 0, 100))
      }}
      onClick={() => setLocked(v => !v)}
    >
      <img className="cmp-base" src={asset(sample.before.src)} alt="处理前" />
      <div className="cmp-top" style={{ clipPath: `inset(0 0 0 ${topClip}%)` }}>
        <img src={asset(sample.after.src)} alt="处理后" />
      </div>
      {view === 'wipe' && <div className="cmp-div" style={{ left: `${pct}%` }} />}
      {showLeftTag && <span className="cmp-tag cmp-tag-l">处理前</span>}
      {showRightTag && <span className="cmp-tag cmp-tag-r">处理后</span>}
    </div>
  )
}
